import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import QuickStartErrorText from './common/errorText';
import PageController from './common/pageController';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';

interface QuickstartProgramVisionProps {
  slug: string;
  goal: string;
  screenNum: number;
  maxScreenNum: number;
  changeScreen: (num: number) => void;
}

const roundedGradientButton = {
  borderRadius: '100px',
  width: '164px',
  color: 'black',
  fontWeight: 500,
  fontSize: '20px',
  textTransform: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '100px',
    padding: '2px', //ajust the padding to change the width of the border radius
    background: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    backgroundImage: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    color: 'white',
  },
};

const QuickstartProgramVision = ({
  slug,
  goal,
  screenNum,
  maxScreenNum,
  changeScreen,
}: QuickstartProgramVisionProps) => {
  const intl = useIntl();
  const [companyGoals, setCompanyGoals] = useState<string>('');
  const [fielderror, setFieldError] = useState<string>('');
  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });

  useEffect(() => {
    setCompanyGoals(goal);
  }, [goal]);

  const setPage = (pageNum: number) => {
    changeScreen(pageNum);
  };

  const isFieldValidated = () => {
    let isValidated = true;
    if (companyGoals?.length === 0) {
      setFieldError(
        intl.formatMessage({ defaultMessage: 'Company Goals is Required' }),
      );
      isValidated = false;
    } else setFieldError('');

    return isValidated;
  };

  const updateCompanyGoalHandler = async () => {
    if (!isFieldValidated()) return;
    setRequestStatus({
      isLoading: true,
      success: false,
      error: false,
      text: '',
    });
    try {
      const reqData = {
        company_goals: companyGoals,
      };
      const response = await axiosInstanceForTenantAPI(slug).patch(
        '/quickstart-details',
        reqData,
      );
      const status = response.status;
      if (status === 200) {
        setRequestStatus({
          isLoading: false,
          success: true,
          error: false,
          text: '',
        });
        setPage(screenNum + 1);
      }
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Tooltip Guide not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
    }
  };

  return (
    <Stack
      sx={{
        padding: '90px 64px',
        minHeight: '100vh',
        scrollSnapAlign: 'center',
      }}
    >
      <PageController
        page={screenNum}
        setPage={setPage}
        totalPage={maxScreenNum}
      />

      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          color: '#181818',
          lineHeight: '150%',
          maxWidth: 650,
          marginBottom: '36px',
        }}
      >
        <FormattedMessage defaultMessage="Share your goals to help  Confide power your company's program." />
      </Typography>
      <Stack marginBottom='64px' gap={0.5}>
        <TextField
          variant='standard'
          multiline
          rows={10}
          sx={{
            border: '1px solid #D1D1D1',
            borderRadius: '6px',
            padding: '16px',
          }}
          placeholder={intl.formatMessage({
            defaultMessage: 'Type your goals here',
          })}
          InputProps={{ disableUnderline: true }}
          value={companyGoals}
          onChange={(e) => setCompanyGoals(e.target.value)}
        />
        {fielderror && <QuickStartErrorText error={fielderror} />}
      </Stack>
      <Button sx={roundedGradientButton} onClick={updateCompanyGoalHandler}>
        {requestStatus.isLoading ? (
          <CircularProgress size={20} sx={{ marginY: 1 }} />
        ) : (
          intl.formatMessage({ defaultMessage: 'Proceed' })
        )}
      </Button>
    </Stack>
  );
};

export default QuickstartProgramVision;
